import React, { useCallback, useContext, useEffect, useState } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import { createPortal } from 'react-dom';
import { Button } from '@mui/material';
import styles from './styles.module.css';
import DataTable from 'components/utils/DataTable';
import BillSummaryReportService from 'services/BillSummaryReportService';
import DateTimeHelpers from 'helpers/DateTimeHelpers';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import SyncIcon from '@mui/icons-material/Sync';
import { saveAs } from 'file-saver';
import 'date-fns';
import Info from 'components/utils/Alert/Info';
import withConsoleBase from 'components/utils/ConsoleBase/withConsoleBase';
import { SUBSCRIPTION_TYPE, PRINT_TYPES } from 'const';
import { useReportPrint } from 'hooks/useReportPrint';
import PrintDocument from 'components/utils/PrintDocument/index';
import { ShopContext } from 'Context/ShopContext';
import CsvHelper from 'helpers/CsvHelper';
import { AlertMessagesContext } from 'react-alert-messages';
import PrintIcon from '@mui/icons-material/Print';
import ShopsHelper from 'helpers/ShopsHelper';
import Grayout from 'components/utils/GrayOut/Grayout';
import DateLimitDialog from 'components/popups/DateLimitDialog';
import DateTimeRange from 'components/utils/DateTimeRange/DateTimeRange';
import { dynamicDateRange } from 'services/DynamicDateRangeService';
import { useTranslation } from 'react-i18next';
import { Grid, TextField, CircularProgress } from '@mui/material';

function StaffSalesReport() {
  const { t: tCommon } = useTranslation('common');
  const { t: tReport } = useTranslation('report');
  const { shop } = useContext(ShopContext);
  const { postAlertMessage } = useContext(AlertMessagesContext);

  const [loadingIndicator, setLoadingIndicator] = useState(false);
  const [filterFromDate, setFilterFromDate] = useState(new Date());
  const [filterToDate, setFilterToDate] = useState(new Date());
  const [prevFilterData, setPrevFilterData] = useState({
    filterFromDate,
    filterToDate,
  });
  const [waiterData, setWaiterData] = useState([]);
  const [cashierData, setCashierData] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [filteredCashierData, setFilteredCashierData] = useState([]);
  const [filteredWaiterData, setFilteredWaiterData] = useState([]);
  const [isShowGenerateButton, setIsShowGenerateButton] = useState(null);
  const [dateLimitPopup, setDateLimitPopup] = useState(false);
  const { reportToPrint, handlePrint: handlePrintReportHook } = useReportPrint(shop, tCommon, tReport, DateTimeHelpers);

  const CSV_COLUMNS = {
    name: tCommon('Name'),
    num_bills: tCommon('Sales'),
    discount: {
      title: tCommon('Discount'),
      getValue: (datum) => {
        return ShopsHelper.getAmountFormatted(shop, datum?.discount || 0);
      },
    },
    total: {
      title: tCommon('Total Amount'),
      getValue: (datum) => {
        return ShopsHelper.getAmountFormatted(shop, datum?.total || 0);
      },
    },
  };

  const isAnyChangeOnReportsFilters = useCallback(() => {
    return filterFromDate !== prevFilterData.filterFromDate || filterToDate !== prevFilterData.filterToDate;
  }, [filterFromDate, filterToDate, prevFilterData.filterFromDate, prevFilterData.filterToDate]);

  useEffect(() => {
    // getMachineName();
    if (subscriptionType && subscriptionType !== SUBSCRIPTION_TYPE.PREMIUM) return;
    generateSummeryReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isShowGenerateButton === null) return;
    setIsShowGenerateButton(isAnyChangeOnReportsFilters());
  }, [isAnyChangeOnReportsFilters, isShowGenerateButton]);

  const handlePrintReport = () => {
    const combinedData = [];
    if (filteredCashierData.length > 0) {
      combinedData.push({ name: `--- ${tReport('Cashier Sales Report')} ---`, isHeader: true });
      combinedData.push(...filteredCashierData);
    }
    if (filteredWaiterData.length > 0) {
      combinedData.push({ name: `--- ${tReport('Waiter Sales Report')} ---`, isHeader: true });
      combinedData.push(...filteredWaiterData);
    }

    handlePrintReportHook(
      tReport('Staff Sales Report'),
      filterFromDate,
      filterToDate,
      headerData,
      combinedData,
      {

        [tCommon('Total Sales')]: `${ShopsHelper.getAmountFormatted(shop, parseFloat(cashierSummary.totalSales) + parseFloat(waiterSummary.totalSales))}`,
        [tCommon('Total Amount')]: `${shop?.currency || ''} ${ShopsHelper.getAmountFormatted(shop, (cashierSummary.totalAmount || 0)+ (waiterSummary.totalAmount || 0))}`,
      }
    );
  };

  const generateSummeryReport = () => {
    const { startDate, endDate } = dynamicDateRange('STAFF_SALES_REPORT');

    setFilterFromDate(startDate);
    setFilterToDate(endDate);

    loadBillSummaryReports({
      startTime: startDate,
      endTime: endDate,
    });

    setIsShowGenerateButton(false);
    setPrevFilterData({
      filterFromDate: startDate,
      filterToDate: endDate,
    });
  };

  const toCsvBtnPressed = async () => {
    // Waiter CSV
    const waiterTitleLine =
      tReport('Staff Sales Report for {{from}} to {{to}}', {
        from: DateTimeHelpers.convertDateToDMY(filterFromDate),
        to: DateTimeHelpers.convertDateToDMY(filterToDate),
      }) +
      ' - ' +
      tReport('Waiter Sales Report');

    const waiterCsvWithoutTotal = CsvHelper.getString(filteredWaiterData, CSV_COLUMNS, waiterTitleLine);
    const waiterSummary = _getWaiterReportSummary(filteredWaiterData);
    const fields = Object.keys(CSV_COLUMNS);

    const waiterTotalRowValues = fields.map((key) => {
      switch (key) {
        case 'name':
          return tCommon('Total');
        case 'num_bills':
          return waiterSummary.totalSales;
        case 'discount':
          return waiterSummary.totalDiscount;
        case 'total':
          return waiterSummary.totalAmount;
        default:
          return '';
      }
    });

    const waiterTotalRow = waiterTotalRowValues.join(',') + '\n';
    const waiterCsv = waiterCsvWithoutTotal + '\n' + waiterTotalRow;

    const waiterBlob = new Blob([waiterCsv], {
      type: 'text/csv',
    });
    saveAs(waiterBlob, `staff-sales-summary-${new Date().toISOString()}.csv`);

    // Cashier CSV
    const cashierTitleLine =
      tReport('Staff Sales Report for {{from}} to {{to}}', {
        from: DateTimeHelpers.convertDateToDMY(filterFromDate),
        to: DateTimeHelpers.convertDateToDMY(filterToDate),
      }) +
      ' - ' +
      tReport('Cashier Sales Report');

    const cashierCsvWithoutTotal = CsvHelper.getString(filteredCashierData, CSV_COLUMNS, cashierTitleLine);
    const cashierSummary = _getCashierReportSummary(filteredCashierData);

    const cashierTotalRowValues = fields.map((key) => {
      switch (key) {
        case 'name':
          return tCommon('Total');
        case 'num_bills':
          return cashierSummary.totalSales;
        case 'discount':
          return cashierSummary.totalDiscount;
        case 'total':
          return cashierSummary.totalAmount;
        default:
          return '';
      }
    });

    const cashierTotalRow = cashierTotalRowValues.join(',') + '\n';
    const cashierCsv = cashierCsvWithoutTotal + '\n' + cashierTotalRow;

    const cashierBlob = new Blob([cashierCsv], {
      type: 'text/csv',
    });
    saveAs(cashierBlob, `cashier-sales-summary-${new Date().toISOString()}.csv`);

    postAlertMessage({
      text: tReport('Exported to excel successfully'),
      type: 'success',
    });
  };

  const loadBillSummaryReports = async ({ startTime = filterFromDate, endTime = filterToDate } = {}) => {
    if (DateTimeHelpers.reportDateLimit(startTime, endTime)) {
      setDateLimitPopup(true);
      return;
    }
    setLoadingIndicator(true);
    try {
      const data = {
        filter_start_time: DateTimeHelpers.getTimezoneOffsetDate(startTime),
        filter_end_time: DateTimeHelpers.getTimezoneOffsetDate(endTime),
      };
      const [waiter_resp, cashier_resp] = await Promise.all([
        BillSummaryReportService.getWaiterSalesReport(data),
        BillSummaryReportService.getCashierSalesReport(data),
      ]);
      const validWaiters = waiter_resp.filter((item) => item.name && item.name.trim() !== '');
      const validCashiers = cashier_resp.filter((item) => item.name && item.name.trim() !== '');
      setWaiterData(validWaiters);
      setCashierData(validCashiers);
      setFilteredWaiterData(validWaiters);
      setFilteredCashierData(validCashiers);
    } catch (error) {
      postAlertMessage({ text: error.message, type: 'failed' });
    }
    setLoadingIndicator(false);
  };

  const handleSearch = (value) => {
    setSearchText(value);
    const searchLower = value.toLowerCase();
    setFilteredCashierData(
      cashierData.filter((item) => item.name && item.name.toString().toLowerCase().includes(searchLower))
    );
    setFilteredWaiterData(
      waiterData.filter((item) => item.name && item.name.toString().toLowerCase().includes(searchLower))
    );
  };

  const subscriptionType = shop?.subscription?.type || '';
  const headerData = [
    {
      label: tCommon('Name'),
      id: 'name',
      type: 'text',
    },
    {
      label: tCommon('Sales'),
      id: 'num_bills',
      type: 'text',
    },
    {
      label: tCommon('Discount'),
      id: 'discount',
      type: 'floatAmount',
    },
    {
      label: tCommon('Total Amount'),
      id: 'total',
      type: 'callback',
      viewRender: (item) => {
        return `${shop?.currency || ''} ${ShopsHelper.getAmountFormatted(shop, item.total)}`;
      },
    },
  ];

  const _getWaiterReportSummary = (items) => {
    let totalSales = 0;
    let totalAmount = 0;
    let totalDiscount = 0;
    items.forEach((item) => {
      totalSales += item.num_bills;
      totalAmount += item.total;
      totalDiscount += item.discount;
    });

    return {
      totalSales: ShopsHelper.getAmountFormatted(shop, totalSales),
      totalAmount: ShopsHelper.getAmountFormatted(shop, totalAmount),
      totalDiscount: ShopsHelper.getAmountFormatted(shop, totalDiscount),
    };
  };

  const _getCashierReportSummary = (items) => {
    let totalSales = 0;
    let totalAmount = 0;
    let totalDiscount = 0;
    items.forEach((item) => {
      totalSales += item.num_bills;
      totalAmount += item.total;
      totalDiscount += item.discount;
    });

    return {
      totalSales: ShopsHelper.getAmountFormatted(shop, totalSales),
      totalAmount: ShopsHelper.getAmountFormatted(shop, totalAmount),
      totalDiscount: ShopsHelper.getAmountFormatted(shop, totalDiscount),
    };
  };

  const waiterSummary = _getWaiterReportSummary(filteredWaiterData);
  const cashierSummary = _getCashierReportSummary(filteredCashierData);


  return (
    <div className={styles.contentWrapper}>
      <div className={styles.titleSec}>
        <span className={styles.title}>
          {tCommon('Reports')}
          <span className={styles.menuTitle}>{tCommon('Generation')}</span>
        </span>
        <div style={{ justifyContent: 'flex-end', marginRight: '10px' }}>
          <div style={{ paddingBottom: '4px' }}>
            <label className={styles.label}>{tCommon('Print Report')}</label>
          </div>
          <Button
            variant="contained"
            color="primary"
            className={styles.actionBtn}
            style={{ backgroundColor: '#00a65a' }}
            onClick={handlePrintReport}
          >
            <PrintIcon className={styles.actionBtnIcon} />
            {tCommon('Print')}
          </Button>
        </div>
        <div style={{ justifyContent: 'flex-end' }}>
          <div style={{ paddingBottom: '4px' }}>
            <label className={styles.label}>{tCommon('Export As')}</label>
          </div>
          <Button
            variant="contained"
            color="primary"
            className={styles.actionBtn}
            style={{ backgroundColor: '#00a65a' }}
            onClick={toCsvBtnPressed}
          >
            <ImportExportIcon className={styles.actionBtnIcon} />
            {tCommon('CSV')}
          </Button>
        </div>
      </div>
      <div className={styles.changeable}>
        <Grid container spacing={1} alignItems="flex-end">
          {/* Report Title */}
          <Grid item xs={12}>
            <h2 className={styles.subTitle}>
              {tReport('Staff Sales Report for {{from}} to {{to}}', {
                from: DateTimeHelpers.convertDateToDMY(filterFromDate),
                to: DateTimeHelpers.convertDateToDMY(filterToDate),
              })}
            </h2>
          </Grid>

          {/* Date Range */}
          <Grid item xs={12} md={3}>
            <DateTimeRange
              setFilterFromDate={setFilterFromDate}
              setFilterToDate={setFilterToDate}
              storageKey="STAFF_SALES_REPORT"
            />
          </Grid>

          {/* Search */}
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              variant="outlined"
              label={tCommon('Search by name')}
              value={searchText}
              onChange={(e) => handleSearch(e.target.value)}
              InputProps={{
                endAdornment: <SearchIcon />,
              }}
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <Button
              variant="contained"
              className={styles.greenBtn}
              onClick={generateSummeryReport}
              disabled={isShowGenerateButton === false || loadingIndicator}
              startIcon={loadingIndicator ? <CircularProgress size={20} color="inherit" /> : <SyncIcon />}
            >
              {tCommon('Generate Report')}
            </Button>
          </Grid>
        </Grid>
      </div>
      {isShowGenerateButton === null ? (
        <Info
          title={tReport('Set filters and click generate button')}
          content={tReport(
            'Reports are generated based on the filters. Please set filters and click generate button to generate the report.'
          )}
        />
      ) : (
        <>
          {cashierData && (filteredCashierData?.length > 0 || !loadingIndicator) && filteredCashierData?.length > 0 && (
            <>
              <h2 className={styles.subTitle} style={{ padding: '20px 5px' }}>
                {tReport('Cashier Sales Report')}
              </h2>
              <div className={styles.tableContainer}>
                <Grayout open={isShowGenerateButton} />
                <DataTable
                  columns={headerData}
                  rows={filteredCashierData || cashierData}
                  rowKey="id"
                  summary={{
                    name: tCommon('Total'),
                    num_bills: `${cashierSummary.totalSales}`,
                    discount: `${shop?.currency || ''}  ${cashierSummary.totalDiscount}`,
                    total: `${shop?.currency} ${cashierSummary.totalAmount}`,
                  }}
                />
              </div>
            </>
          )}

          {waiterData && (filteredWaiterData?.length > 0 || !loadingIndicator) && filteredWaiterData?.length > 0 && (
            <>
              <h2 className={styles.subTitle} style={{ padding: '20px 5px' }}>
                {tReport('Waiter Sales Report')}
              </h2>
              <div className={styles.tableContainer}>
                <Grayout open={isShowGenerateButton} />
                <DataTable
                  columns={headerData}
                  rows={filteredWaiterData || waiterData}
                  rowKey="id"
                  summary={{
                    name: tCommon('Total'),
                    num_bills: `${waiterSummary.totalSales}`,
                    discount: `${shop?.currency || ''}  ${waiterSummary.totalDiscount}`,
                    total: `${shop?.currency} ${waiterSummary.totalAmount}`,
                  }}
                />
              </div>
            </>
          )}
          {!loadingIndicator && !filteredCashierData?.length && !filteredWaiterData?.length && (
            <Info
              severity="warning"
              title={tReport('Oh no, there are no sales matching to your filter')}
              content={tReport(
                'There are no sales matching to your filter. Please verify your inputs to generate report.'
              )}
            />
          )}
        </>
      )}
      {dateLimitPopup && (
        <DateLimitDialog
          handleClose={() => {
            setDateLimitPopup(false);
          }}
        />
      )}
      {reportToPrint &&
        createPortal(<PrintDocument type={PRINT_TYPES.REPORT} data={reportToPrint} shop={shop} />, document.body)}
    </div>
  );
}

export default withConsoleBase(StaffSalesReport);
